/* eslint-disable complexity */
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { handleAxiosError } from 'src/api';
import { isValidCasePath, isValidDisclosurePath } from 'src/utils';
import { RESETS } from '../../api';
import axios from '../../axios';
import {
  get2FACode,
  getOrgAuthenticationType,
  setEmail,
  setFlow,
} from '../../creators/Login.creators';
import { create2FASession, createSession } from '../../creators/User.creators';
import '../../css/login/Login.css';
import {
  AuthenticationType,
  LoginFlow,
  ResHubLoginNavigationState,
  State,
  User,
} from '../../types';
import { handleErrorToast, handleSuccessToast } from '../molecules';
import { NavbarPermissions } from '../organisms';
import { useCanAccessSpeakUp } from '../../hooks/useCanAccessSpeakUp';
import { LoginWrapper } from '../pages/Router/InternalRouting/InternalRouting.styles';
import LoginCode from './screens/LoginCode';
import LoginEmail from './screens/LoginEmail';
import LoginPassword from './screens/LoginPassword';
import SSOLoginRedirect from './screens/sso/SSOLoginRedirect';

interface OwnProps {
  expired: boolean;
  segmentTracking?: boolean;
}

interface StateProps {
  authenticationType: string | null;
  slug: string | null;
  user: User | null;
  email: string;
  flow: LoginFlow;
}
interface DispatchProps {
  getOrgAuthenticationType: (email: string, redirectURL?: string) => void;
  createSession: (
    username: string,
    password: string,
    segmentTracking?: boolean,
  ) => Promise<void>;
  get2FACode: (username: string, password: string) => Promise<boolean>;
  create2FASession: (
    username: string,
    password: string,
    code: string,
    segmentTracking?: boolean,
  ) => Promise<void>;
  setEmail: (email: string) => void;
  setFlow: (flow: LoginFlow) => void;
}

type Props = OwnProps & StateProps & DispatchProps;
export const ResolutionHubLoginComponent = (props: Props) => {
  const location = useLocation<ResHubLoginNavigationState>();
  const redirectToNext = location.state
    ? location.state.redirectToNext
    : undefined;

  const currentUser = useSelector(
    (state: State) => state.globalUsers.currentUser,
  );

  const canAccessSpeakUp = useCanAccessSpeakUp();

  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [redirect, setRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState('');

  const login = async () => {
    setIsLoading(true);
    if (props.authenticationType === AuthenticationType.VAULT_LOGIN) {
      try {
        await props.createSession(props.email, password, props.segmentTracking);
        setRedirect(true);
      } catch (errors) {
        const message = (errors as Error[])[0]?.message;
        if (message === undefined) {
          handleErrorToast({
            toastMessage: 'Login failed, email and/or password is incorrect.',
          });
        } else if (message === 'Password reset required.') {
          enforcePasswordReset();
        } else {
          handleErrorToast({ toastMessage: message });
        }
        setIsLoading(false);
      }
    } else {
      await props.get2FACode(props.email, password);
      setIsLoading(false);
    }
  };

  const enforcePasswordReset = async () => {
    try {
      const results = await axios.post(RESETS, {
        username: props.email,
        skipNotify: true,
      });
      const token = results.data.data.token;
      if (!token) {
        throw new Error('Could not get token.');
      }
      setRedirectTo(`/reset/update-password/${token}`);
      setRedirect(true);
    } catch (error) {
      const errors = handleAxiosError(
        error as AxiosError<{ errors: { message: string }[] }>,
      );
      if (Array.isArray(errors)) {
        handleErrorToast({ toastMessage: errors[0].message });
      } else if ((error as Error).message) {
        handleErrorToast({ toastMessage: (error as Error).message });
      } else {
        handleErrorToast({ toastMessage: 'Could not enforce password reset.' });
      }
    }
  };

  const loginCode = async () => {
    setIsLoading(true);
    try {
      await props.create2FASession(
        props.email,
        password,
        code,
        props.segmentTracking,
      );
      setRedirect(true);
    } catch (errors) {
      if ((errors as Error[])?.[0]?.message === undefined) {
        handleErrorToast({
          toastMessage: 'Login failed, the code is incorrect or has expired.',
        });
      } else {
        handleErrorToast({ toastMessage: (errors as Error[])[0].message });
      }
    }

    setIsLoading(false);
  };

  const resendCode = async () => {
    const result = await props.get2FACode(props.email, password);
    if (result) {
      handleSuccessToast({
        toastMessage: `A new code has been sent to ${props.email}`,
      });
    } else {
      handleErrorToast({
        toastMessage: `Something went wrong, please try again.`,
      });
    }
  };

  const setRedirectForPermissions = useCallback(() => {
    if (currentUser?.systemPermissions?.includes(NavbarPermissions.NAV_CASES)) {
      if (canAccessSpeakUp) {
        setRedirectTo(() =>
          redirectToNext && isValidCasePath(redirectToNext)
            ? redirectToNext
            : '/cases',
        );
      } else {
        setRedirectTo(() =>
          redirectToNext && isValidDisclosurePath(redirectToNext)
            ? redirectToNext
            : '/disclosures',
        );
      }
    } else if (
      currentUser?.systemPermissions?.includes(
        NavbarPermissions.NAV_DISCLOSURES,
      )
    ) {
      setRedirectTo(() =>
        redirectToNext && isValidDisclosurePath(redirectToNext)
          ? redirectToNext
          : '/disclosures',
      );
    } else {
      setRedirectTo('/users');
    }
  }, [redirectToNext, currentUser, canAccessSpeakUp]);

  useEffect(() => {
    setRedirectForPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.user, props.user?.permissions, canAccessSpeakUp]);

  return (
    <LoginWrapper>
      {redirect &&
        (props.user || redirectTo.includes('reset/update-password')) && (
          <Redirect to={redirectTo} />
        )}

      {props.expired && (
        <p className="login-paragraph">
          {
            'For security, you have been automatically logged out due to inactivity.'
          }
        </p>
      )}

      {props.flow === LoginFlow.USERNAME && (
        <LoginEmail
          email={props.email}
          onEmailChange={(val: string) => props.setEmail(val)}
          handleOnClick={() =>
            props.getOrgAuthenticationType(props.email, redirectToNext)
          }
        />
      )}

      {props.flow === LoginFlow.PASSWORD && (
        <LoginPassword
          email={props.email}
          password={password}
          setFlow={(val: LoginFlow) => props.setFlow(val)}
          onPasswordChange={(val: string) => setPassword(val)}
          handleOnClick={() => login()}
          loading={isLoading}
        />
      )}

      {props.flow === LoginFlow.CODE && (
        <LoginCode
          email={props.email}
          code={code}
          onCodeChange={(val: string) => setCode(val)}
          loading={isLoading}
          setFlow={(val: LoginFlow) => props.setFlow(val)}
          resendCode={() => {
            resendCode();
          }}
          loginWithCode={() => loginCode()}
        />
      )}

      {props.flow === LoginFlow.SSO && (
        <SSOLoginRedirect authenticationType={props.authenticationType} />
      )}
    </LoginWrapper>
  );
};

const mapStateToProps = (state: State) => {
  const { flow } = state.login;
  const { email } = state.login;
  const user = state.globalUsers && state.globalUsers.currentUser;
  const authenticationType =
    state.organisation?.organisation?.authenticationType ?? null;
  const slug = state.organisation?.organisation?.slug ?? null;

  return {
    email,
    flow,
    user,
    authenticationType,
    slug,
  };
};

export const ResolutionHubLogin = connect(mapStateToProps, {
  getOrgAuthenticationType,
  get2FACode,
  setEmail,
  setFlow,
  createSession,
  create2FASession,
})(ResolutionHubLoginComponent);

export default ResolutionHubLogin;
